import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useStartTeamSessionNow } from "@/lib/teamRecordQueries";
import AddTeamSessionsModal from "./AddTeamSessionsModal";

/**
 * Clients Restructure — Team 360 -> Sessions -> "Start Session Now".
 * Mirrors Client 360's instant-start flow (session_repository.
 * start_team_session_now) — TidyCal is never called; the Session
 * starts in ~1 minute with exactly the selected participants.
 * Complete Session Lifecycle slice: now also routes the coach to the
 * existing Prepare Session flow on success, exactly like the
 * Individual flow (client360/tabs/SessionsTab.jsx's handleStartNow).
 */
export default function StartTeamSessionModal({ workspaceId, teamMembers, onClose }) {
  const navigate = useNavigate();
  const [participantIds, setParticipantIds] = useState(teamMembers.map((m) => m.membership_id));
  const [showAddCredits, setShowAddCredits] = useState(false);
  const startNow = useStartTeamSessionNow(workspaceId);

  function toggleParticipant(id) {
    setParticipantIds((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  }

  async function handleConfirm() {
    if (participantIds.length === 0) return;
    try {
      await startNow.mutateAsync({ participant_member_ids: participantIds });
      toast.success("Team session started");
      onClose();
      navigate(`/rehearsal-room/coach/team/${workspaceId}/prep`);
    } catch (err) {
      if (err.status === 402) {
        setShowAddCredits(true);
        return;
      }
      toast.error(err.body?.detail || "Couldn't start the team session");
    }
  }

  return (
    <>
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-lg" data-testid="start-team-session-modal">
          <DialogHeader>
            <DialogTitle className="font-serif">Start session now</DialogTitle>
            <DialogDescription>Starts an unscheduled team Session immediately — no TidyCal booking.</DialogDescription>
          </DialogHeader>

          <div>
            <label className="text-xs text-ink-soft uppercase mb-1.5 block">Participants</label>
            <div className="grid grid-cols-2 gap-2" data-testid="start-team-session-participants-grid">
              {teamMembers.map((m) => {
                const selected = participantIds.includes(m.membership_id);
                return (
                  <button
                    key={m.membership_id}
                    type="button"
                    onClick={() => toggleParticipant(m.membership_id)}
                    className={`flex items-center justify-between gap-2 border rounded-lg px-3 py-2 text-sm text-left transition-colors ${selected ? "border-terracotta bg-terracotta/10 text-ink" : "border-ink/10 text-ink-soft hover:border-ink/20"}`}
                    data-testid={`start-team-session-participant-${m.membership_id}`}
                  >
                    <span>{m.display_name}</span>
                    {selected && <Check size={14} className="text-terracotta shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>

          <DialogFooter>
            <button onClick={onClose} className="text-sm text-ink/60 px-4 py-2" data-testid="start-team-session-cancel-btn">Cancel</button>
            <button
              onClick={handleConfirm}
              disabled={participantIds.length === 0 || startNow.isPending}
              className="bg-terracotta text-paper text-sm font-medium px-4 py-2 rounded-md hover:bg-terracotta/90 disabled:opacity-50"
              data-testid="start-team-session-confirm-btn"
            >
              {startNow.isPending ? "Starting…" : "Start now"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {showAddCredits && (
        <AddTeamSessionsModal
          workspaceId={workspaceId}
          onClose={() => setShowAddCredits(false)}
          onSuccess={() => { setShowAddCredits(false); handleConfirm(); }}
          nested
        />
      )}
    </>
  );
}
